import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LockIcon } from 'lucide-react';
import { AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminMetricCard, AdminPageTitle, AdminTabs, ConfirmationModal, Panel, StatusBadge } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { Button } from '../../components/ui/Button';
import { adminBadges, levelDistribution, progressionRules, recordAudit, streakBuckets, xpDistribution, type AdminBadge } from '../../data/admin';
import { useLocale } from '../../contexts/LocaleContext';
import { ConfigParamsPanel } from '../../components/admin/ConfigParamsPanel';
import { FixedRulesPanel } from '../../components/admin/FixedRulesPanel';
import { ChallengeAdmin } from '../../components/admin/ChallengeAdmin';
import { engagementAdminSections, type EngagementAdminSection } from '../../data/engagementAdmin';
import { hasCapability, prototypeAdminRole } from '../../data/adminOperations';
const tabs = [{
  id: 'xp',
  label: 'Bee'
}, {
  id: 'niveis',
  label: 'Níveis'
}, {
  id: 'sequencias',
  label: 'Sequências'
}, {
  id: 'medalhas',
  label: 'Medalhas'
}, {
  id: 'desafios',
  label: 'Desafios'
}, {
  id: 'protecao',
  label: 'Proteção de Sequência'
}, {
  id: 'colmeia',
  label: 'Minha Colmeia'
}, {
  id: 'bau',
  label: 'Baú da Colmeia'
}, {
  id: 'liga',
  label: 'Liga'
}];
const engagementTabs: Record<string, EngagementAdminSection> = {
  protecao: 'protection',
  colmeia: 'hive',
  bau: 'chest',
  liga: 'league'
};
export function AdminGamification() {
  const {
    tab: param
  } = useParams();
  const {
    tl
  } = useLocale();
  const navigate = useNavigate();
  const tab = tabs.some(item => item.id === param) ? param as string : 'xp';
  const [editing, setEditing] = useState<AdminBadge | null>(null);
  const badgeColumns: Column<AdminBadge>[] = [{
    id: 'name',
    header: 'Medalha',
    sortValue: row => row.name,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-1565" data-mp="248epasuifgs3">{row.name}</span>
  }, {
    id: 'rule',
    header: 'Regra de qualificação',
    width: '34%',
    cell: row => <span className="text-[12.5px] text-muted" data-id="element-1566" data-mp="1uexupmmjtxnd">{tl(row.rule)}</span>
  }, {
    id: 'unlocked',
    header: 'Desbloqueios',
    align: 'right',
    sortValue: row => Number(row.unlocked.replace(/\./g, '')),
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-1567" data-mp="1ggravaefpy6p">{row.unlocked}</span>
  }, {
    id: 'status',
    header: 'Status',
    cell: row => <StatusBadge status={row.status} label={row.status === 'active' ? 'Ativa' : 'Rascunho'} data-id="element-1568" data-mp="1bi6g3qhyc6pn" />
  }, {
    id: 'created',
    header: 'Criada em',
    sortValue: row => row.created,
    cell: row => <span className="bee-num text-muted" data-id="element-1569" data-mp="35tl6m9isea6g">{row.created}</span>
  }];
  return <div className="space-y-4" data-id="element-1570" data-mp="30xdzweikgung">
      <AdminPageTitle title="Gamificação" subtitle="Distribuição de Bee, níveis, sequências, medalhas e recursos de engajamento. Regras fixas do produto são somente leitura; parâmetros configuráveis exigem permissão." data-id="element-1571" data-mp="39dlda8e9z0nt" />

      <AdminTabs tabs={tabs} active={tab} onChange={next => navigate(`/admin/gamificacao/${next}`)} data-id="element-1572" data-mp="3i785xpn6rnss" />

      {tab === 'xp' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1573" data-mp="3n6uj5z30l41l">
            <AdminMetricCard label="Bee médio por usuário" value="74,2" delta={0.9} accent data-id="element-1574" data-mp="2qgbwkz0c41a9" />
            <AdminMetricCard label="Usuários em 100 Bee hoje" value="31.208" delta={6.7} data-id="element-1575" data-mp="2ugumuomkwzac" />
            <AdminMetricCard label="Acima do gate de 50 Bee" value="81.040" delta={4.2} data-id="element-1576" data-mp="2zenttjvphxbo" />
            <AdminMetricCard label="Bônus de foto usados" value="12.744" delta={2.8} data-id="element-1577" data-mp="34fjt1k544var" />
          </section>

          <AdminBarChart title="Distribuição de Bee no dia" question="Quantos usuários param antes do gate de 50 Bee?" data={xpDistribution} xKey="range" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1578" data-mp="eoexfr99lp5h" />

          <Panel title="Regras de progressão" hint="Configuração vigente, em modo somente leitura." data-id="element-1579" data-mp="1nmof2xs3tegz">
            <ul className="space-y-2" data-id="element-1580" data-mp="21m6avx3thrzu">
              {progressionRules.map(rule => <li key={rule.id} className="flex items-center justify-between gap-3 border-b border-sand-line pb-2 last:border-b-0 last:pb-0" data-id="element-1581" data-mp="1wmbkdunyf4fb">
                  <span className="text-[12.5px] text-deep" data-id="element-1582" data-mp="26liwkeiyct0u">{tl(rule.label)}</span>
                  <span className="flex items-center gap-2" data-id="element-1583" data-mp="2kce7rxgxbm7l">
                    <span className="bee-num text-[12.5px] font-bold text-deep" data-id="element-1584" data-mp="2kbw62lbhsfsa">
                      {rule.value}
                    </span>
                    <LockIcon size={13} className="text-muted" aria-hidden="true" data-id="element-1585" data-mp="2uabwsn6lxn19" />
                  </span>
                </li>)}
            </ul>
            <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-1586" data-mp="2pdbqqc9rcka5">
              A base de 100 Bee, o gate de 50 Bee e o bônus de +10 Bee fora da base são regras
              críticas do produto. A edição exige aprovação de Super Admin e não é exposta
              aqui.
            </p>
          </Panel>
        </>}

      {tab === 'niveis' && <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1587" data-mp="33xehib5dubwq">
          <AdminBarChart title="Usuários por nível" question="A base está avançando de nível ou concentrada no início?" data={levelDistribution} xKey="level" series={[{
        id: 'users',
        name: 'Usuários'
      }]} data-id="element-1588" data-mp="3400kdxhbw38m" />
          <Panel title="Proporção por nível" data-id="element-1589" data-mp="2gag1tfp377zv">
            <DistributionList items={levelDistribution.map(level => ({
          label: level.level,
          value: level.users,
          secondary: level.users.toLocaleString('pt-BR')
        }))} data-id="element-1590" data-mp="2l76hi1wzc1kr" />
          </Panel>
        </div>}

      {tab === 'sequencias' && <>
          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1591" data-mp="hsdxn2enr1ao">
            <AdminMetricCard label="Sequências ativas" value="31.208" delta={6.7} accent data-id="element-1592" data-mp="czt5s6fbay9s" />
            <AdminMetricCard label="Média da sequência ativa" value="5,4 dias" delta={1.2} data-id="element-1593" data-mp="80gjbmscrt7q" />
            <AdminMetricCard label="Quebras ontem" value="2.917" delta={-3.1} data-id="element-1594" data-mp="32f45cy1se1j" />
            <AdminMetricCard label="Acima de 30 dias" value="228" delta={9.4} data-id="element-1595" data-mp="10pnimnzmsmu1" />
          </section>
          <AdminBarChart title="Sequências por faixa" question="Onde as sequências costumam quebrar?" data={streakBuckets} xKey="bucket" series={[{
        id: 'users',
        name: 'Usuários'
      }]} horizontal data-id="element-1596" data-mp="vqkohyl6w10u" />
        </>}

      {tab === 'medalhas' && <>
          <DataTable rows={adminBadges} columns={badgeColumns} rowKey={row => row.id} pageSize={10} actions={[{
        id: 'edit',
        label: 'Editar regra',
        onSelect: setEditing
      }]} data-id="element-1597" data-mp="3birpq7wxkjf7" />
          <Panel title="Edição de medalhas" data-id="element-1598" data-mp="1qz65in9djzq3">
            <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1599" data-mp="1r07kv3xfb8dm">
              Alterar a regra de uma medalha muda a qualificação de toda a base. Por isso a
              edição exige confirmação explícita e fica registrada na auditoria com a regra
              anterior e a nova.
            </p>
          </Panel>
        </>}

      {tab === 'desafios' && <ChallengeAdmin data-id="element-1600" data-mp="2amvqobmt31qc" />}

      {engagementTabs[tab] && <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_1.4fr]" data-id="element-1601" data-mp="2aphxua5sbf81">
          <FixedRulesPanel rules={engagementAdminSections[engagementTabs[tab]].fixedRules} data-id="element-1602" data-mp="2eps1vha0nvk9" />
          <ConfigParamsPanel key={tab} section={tl(engagementAdminSections[engagementTabs[tab]].title)} params={engagementAdminSections[engagementTabs[tab]].params} canEdit={hasCapability(prototypeAdminRole, 'engagement.manageConfig')} data-id="element-1603" data-mp="2jntk5zzmbquo" />
        </div>}

      <ConfirmationModal open={editing !== null} title={`Editar a regra de ${editing?.name ?? ''}?`} body="A nova regra revalida a qualificação de toda a base, o que pode conceder ou remover a medalha de usuários já atendidos. A alteração fica registrada na auditoria." confirmLabel="Entendi, continuar" onConfirm={() => {
      if (editing) {
        recordAudit({
          action: {
            'pt-BR': 'Editou regra de medalha',
            'en-US': 'Edited badge rule'
          },
          entity: `badge:${editing.id}`,
          context: {
            'pt-BR': editing.name,
            'en-US': editing.name
          },
          before: tl(editing.rule),
          severity: 'sensitive'
        });
      }
      setEditing(null);
    }} onClose={() => setEditing(null)} data-id="element-1604" data-mp="2r61frgzq4oyb" />
    </div>;
}